import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Banner, Button, Drawer, Icon, Logo, StatusDot, navPillClass } from '@acs/ui';
import type { IconName } from '@acs/ui';
import { useI18n } from '../i18n/index.js';
import type { TranslationKey } from '../i18n/index.js';
import { BusinessSwitcher } from '../features/businesses/index.js';
import { useSetupStatus } from '../features/onboarding/index.js';
import type { SetupTask } from '../features/onboarding/index.js';
import { useActiveBusiness } from './ActiveBusinessLayout.js';
import { AppHeader } from './AppHeader.js';

interface NavItem {
  to: string;
  labelKey: TranslationKey;
  icon: IconName;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '', labelKey: 'nav.dashboard', icon: 'calendar', end: true },
  { to: 'calls', labelKey: 'nav.calls', icon: 'phone' },
  { to: 'clients', labelKey: 'nav.clients', icon: 'users' },
  { to: 'staff', labelKey: 'nav.staff', icon: 'user' },
  { to: 'settings', labelKey: 'nav.settings', icon: 'settings' },
];

/** Businesses in these states can't answer calls yet — the shell says so.
 *  Copy lives in the catalogue under `attention.<status>.*` (doc 20 D8). */
const NEEDS_ATTENTION = new Set([
  'draft',
  'collecting_docs',
  'pending_review',
  'rejected',
  'approved',
  'provisioning',
  'suspended',
]);

export function Layout() {
  const { t } = useI18n();
  const business = useActiveBusiness();
  const base = `/b/${business.id}`;
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Dismissal is per business and per session: a reload shouldn't nag again,
  // but a new session (or another business) should.
  const [dismissed, setDismissed] = useState(() => readDismissed(business.id));

  useEffect(() => setDrawerOpen(false), [location.pathname]);
  useEffect(() => setDismissed(readDismissed(business.id)), [business.id]);

  // Keep the active business identifiable in the tab title (doc 08 §9) — also
  // disambiguates two tabs open on two businesses.
  useEffect(() => {
    document.title = `${business.name} — TopCall24`;
    return () => {
      document.title = 'TopCall24';
    };
  }, [business.name]);

  const attention = NEEDS_ATTENTION.has(business.status) ? business.status : null;
  const onNumberSettings = location.search.includes('section=number') && location.pathname === `${base}/settings`;

  // Read here rather than inside the banner so the shell can pass *nothing*
  // when there is nothing outstanding: `AppBar` renders its banner strip for
  // any truthy node, and a component that returns null still leaves the strip's
  // top border ruled under the header.
  const setupStatus = useSetupStatus(business);

  const navLinks = (variant: 'bar' | 'drawer') =>
    NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to ? `${base}/${item.to}` : base}
        end={item.end}
        className={({ isActive }) => navPillClass(isActive, variant)}
      >
        {variant === 'drawer' && <Icon name={item.icon} size={18} />}
        {t(item.labelKey)}
        {item.to === 'settings' && attention && <StatusDot tone="warning" label={t('nav.needsAttention')} />}
      </NavLink>
    ));

  return (
    <div className="app-shell">
      <AppHeader
        onOpenMenu={() => setDrawerOpen(true)}
        switcher={<BusinessSwitcher linkHome />}
        nav={<nav className="tc-navnav">{navLinks('bar')}</nav>}
        banner={
          // No setup banner here any more: the gate in `ActiveBusinessLayout`
          // means this shell only ever renders for a business whose setup is
          // finished, so a "you still have steps left" strip could never show.
          attention && !dismissed && !onNumberSettings ? (
            <Banner
              tone={business.status === 'rejected' || business.status === 'suspended' ? 'danger' : 'warning'}
              flush
              onClose={() => {
                setDismissed(true);
                writeDismissed(business.id);
              }}
              actions={
                <Button size="sm" onClick={() => navigate(`${base}/settings?section=number`)}>
                  {t(`attention.${attention}.cta` as TranslationKey)}
                </Button>
              }
            >
              <span>
                <strong>{t('attention.actionNeeded')}</strong> {t(`attention.${attention}.text` as TranslationKey)}
              </span>
            </Banner>
          ) : setupStatus.next ? (
            <ChecklistBanner
              businessId={business.id}
              next={setupStatus.next}
              alsoLeft={setupStatus.missing.length - 1}
            />
          ) : undefined
        }
      />

      <Drawer
        closeLabel={t('ui.closeDialog')}
        open={drawerOpen}
        side="left"
        onClose={() => setDrawerOpen(false)}
        // The drawer opens from inside a business, so it says which one — the
        // top bar's switcher is behind the overlay while it is open, and the
        // mark alone would leave the panel unattributed.
        title={
          <span className="drawer-brand">
            <Logo markOnly size="sm" />
            <span className="drawer-brand__name">{business.name}</span>
          </span>
        }
        aria-label={t('nav.navigation')}
      >
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{navLinks('drawer')}</nav>
      </Drawer>

      {/* Keyed on the business: switching businesses REMOUNTS the page, it does
          not just re-render it. Every editor under here seeds its form state
          from the business doc at mount (`useState(business.name)`), so a
          re-render left Settings showing the previous business's name — with a
          Save bar offering to write it onto the new one. The key is on the page
          slot rather than the shell so the header, switcher and banner don't
          flash on each switch. */}
      <main className="page">
        <Outlet key={business.id} />
      </main>
    </div>
  );
}

const dismissKey = (businessId: string) => `tc24.attentionDismissed.${businessId}`;

function readDismissed(businessId: string): boolean {
  try {
    return sessionStorage.getItem(dismissKey(businessId)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(businessId: string): void {
  try {
    sessionStorage.setItem(dismissKey(businessId), '1');
  } catch {
    /* private mode — the banner just comes back next session */
  }
}

/**
 * Shown once setup is *behind* the owner but the configuration still has holes
 * — most often a business that finished the wizard and never added staff.
 *
 * It names one task, not a list: the checklist is ordered so the first
 * outstanding entry is the one worth doing next, and a banner offering five
 * links is a banner nobody clicks. The count of what's left goes in the text so
 * the owner can see the end of it.
 *
 * The shell only mounts it when there is a task — while the checklist is still
 * loading, and once everything is configured, the header carries no strip at
 * all. A shell that is silent for a finished business is what makes this worth
 * reading for an unfinished one.
 */
function ChecklistBanner({
  businessId,
  next,
  alsoLeft,
}: {
  businessId: string;
  next: SetupTask;
  /** How many tasks follow this one — shown so the owner can see the end of it. */
  alsoLeft: number;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <Banner
      tone={next.blocking ? 'danger' : 'warning'}
      flush
      actions={
        <Button size="sm" onClick={() => navigate(`/b/${businessId}${next.href}`)}>
          {t(next.ctaKey)}
        </Button>
      }
    >
      <span>
        <strong>{next.blocking ? t('checklist.blocking') : t('checklist.unfinished')}</strong>{' '}
        {t(next.detailKey)}
        {alsoLeft > 0 && t('checklist.alsoLeft', { count: alsoLeft })}
      </span>
    </Banner>
  );
}
